import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Drawer,
  useMediaQuery,
  useTheme,
  Fab,
  Skeleton,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper
} from '@mui/material';
import {
  FilterList,
  ViewModule,
  ViewList,
  Sort,
  AccountBalance,
  Person,
  Business,
  TrendingUp,
  Payment,
  CheckCircle
} from '@mui/icons-material';
import OrganisationCard from '../components/OrganisationCard';
import OrganisationFilters from '../components/OrganisationFilters';
import Breadcrumbs from '../components/Breadcrumbs';
import Pagination from '../components/Pagination';
import {
  selectOrganizations,
  selectLoading,
  selectError,
  selectPagination,
  setSortBy,
  setCurrentPage,
  fetchOrganizations
} from '../redux/slices/organisationsSlice';
import './Organisations.css';

const Organisations = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const organizations = useSelector(selectOrganizations);
  const pagination = useSelector(selectPagination);
  const { sortBy, filters } = useSelector(state => state.organisations);
  
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const breadcrumbItems = [
    { label: 'Accueil', href: '/' },
    { label: 'Annuaire', href: '/organisations' }
  ];

  const sortOptions = [
    { value: 'name', label: 'Nom A-Z' },
    { value: 'name-desc', label: 'Nom Z-A' },
    { value: 'rating', label: 'Mieux notées' },
    { value: 'missions', label: 'Plus de missions' },
    { value: 'members', label: 'Plus de membres' },
    { value: 'date', label: 'Plus récentes' }
  ];

  // Fetch organizations on mount
  useEffect(() => {
    dispatch(fetchOrganizations());
  }, [dispatch]);

  // Update page title based on filters
  useEffect(() => {
    let title = 'Annuaire des organisations sportives';
    if (filters.search) {
      title += ` - ${filters.search}`;
    }
    if (filters.location) {
      title += ` à ${filters.location}`;
    }
    document.title = `${title} | Mission Sport`;
  }, [filters]);

  const handleSortChange = (event) => {
    dispatch(setSortBy(event.target.value));
  };

  const handlePageChange = (page) => {
    dispatch(setCurrentPage(page));
    dispatch(fetchOrganizations());
    // Scroll to top on page change
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFiltersToggle = () => {
    setFiltersOpen(!filtersOpen);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setSnackbar({
      open: true,
      message: `Mode d'affichage changé: ${mode === 'grid' ? 'Grille' : 'Liste'}`,
      severity: 'info'
    });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.location) count++;
    if (filters.type) count++;
    if (filters.disciplines?.length > 0) count++;
    if (filters.verified) count++;
    return count;
  };

  const renderSkeletons = () => {
    return Array.from({ length: 12 }).map((_, index) => (
      <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
          <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1, mb: 2 }} />
          <Skeleton variant="circular" width={60} height={60} sx={{ mb: 2 }} />
          <Skeleton variant="text" height={32} sx={{ mb: 1 }} />
          <Skeleton variant="text" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" height={20} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
          </Box>
          <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
        </Paper>
      </Grid>
    ));
  };

  const renderEmptyState = () => (
    <Box 
      sx={{ 
        textAlign: 'center', 
        py: 8,
        px: 2
      }}
    >
      <Typography variant="h5" gutterBottom color="text.secondary">
        Aucune organisation trouvée
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {getActiveFiltersCount() > 0 
          ? 'Essayez de modifier vos critères de recherche'
          : 'Aucune organisation disponible pour le moment'
        }
      </Typography>
      {getActiveFiltersCount() > 0 && (
        <Button 
          variant="outlined" 
          onClick={() => dispatch({ type: 'organisations/clearFilters' })}
          sx={{ textTransform: 'none' }}
        >
          Effacer les filtres
        </Button>
      )}
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>Annuaire des organisations sportives | Mission Sport</title>
        <meta 
          name="description" 
          content="Consultez l'annuaire des organisations sportives partenaires : clubs, fédérations, salles de sport et organisateurs d'événements. Trouvez votre prochaine mission sportive."
        />
        <meta name="keywords" content="organisations sportives, clubs, fédérations, salles de sport, missions sport" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Annuaire des organisations sportives | Mission Sport" />
        <meta property="og:description" content="Consultez l'annuaire des organisations sportives partenaires de Mission Sport" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://missionsport.fr/organisations" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Annuaire des organisations sportives | Mission Sport" />
        <meta name="twitter:description" content="Consultez l'annuaire des organisations sportives partenaires de Mission Sport" />
      </Helmet>

      <Box className="organisations-page">
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs items={breadcrumbItems} sx={{ mb: 3 }} />

          {/* Header */}
          <Box className="page-header" sx={{ mb: 4 }}>
            <Box>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 1
                }}
              >
                Annuaire des organisations
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontWeight: 400 }}
              >
                Découvrez {pagination.totalItems} organisations partenaires
              </Typography>
            </Box>
          </Box>

          {/* Filters Section */}
          {!isMobile && (
            <Paper elevation={0} sx={{ mb: 4, border: '1px solid', borderColor: 'divider' }}>
              <OrganisationFilters />
            </Paper>
          )}

          {/* Controls Bar */}
          <Box 
            className="controls-bar" 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 3,
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              {/* Mobile Filters Button */}
              {isMobile && (
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={handleFiltersToggle}
                  sx={{ textTransform: 'none' }}
                >
                  Filtres
                  {getActiveFiltersCount() > 0 && (
                    <Chip 
                      label={getActiveFiltersCount()} 
                      size="small" 
                      color="primary" 
                      sx={{ ml: 1, minWidth: 20, height: 20 }}
                    />
                  )}
                </Button>
              )}

              {/* Results Count */}
              <Typography variant="body2" color="text.secondary">
                {loading ? 'Chargement...' : `${pagination.totalItems} organisation(s) trouvée(s)`}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* View Mode Toggle */}
              <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Button
                  size="small"
                  onClick={() => handleViewModeChange('grid')}
                  sx={{ 
                    minWidth: 40,
                    bgcolor: viewMode === 'grid' ? 'primary.main' : 'transparent',
                    color: viewMode === 'grid' ? 'white' : 'text.secondary',
                    '&:hover': {
                      bgcolor: viewMode === 'grid' ? 'primary.dark' : 'action.hover'
                    }
                  }}
                >
                  <ViewModule />
                </Button>
                <Button
                  size="small"
                  onClick={() => handleViewModeChange('list')}
                  sx={{ 
                    minWidth: 40,
                    bgcolor: viewMode === 'list' ? 'primary.main' : 'transparent',
                    color: viewMode === 'list' ? 'white' : 'text.secondary',
                    '&:hover': {
                      bgcolor: viewMode === 'list' ? 'primary.dark' : 'action.hover'
                    }
                  }}
                >
                  <ViewList />
                </Button>
              </Box>

              {/* Sort Dropdown */}
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ color: 'white' }}>Trier par</InputLabel>
                <Select
                  value={sortBy}
                  label="Trier par"
                  onChange={handleSortChange}
                  startAdornment={<Sort sx={{ mr: 1, color: 'text.secondary' }} />}
                  sx={{
                    color: 'white',
                    backgroundColor: 'transparent',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.23)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.5)'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    },
                    '& .MuiSelect-icon': {
                      color: 'white'
                    }
                  }}
                >
                  {sortOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Organisations Grid */}
          {loading ? (
            <Grid container spacing={3}>
              {renderSkeletons()}
            </Grid>
          ) : organizations.length === 0 ? (
            renderEmptyState()
          ) : (
            <Grid container spacing={3}>
              {organizations.map((organisation) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={viewMode === 'list' ? 12 : 6} 
                  md={viewMode === 'list' ? 12 : 4} 
                  lg={viewMode === 'list' ? 12 : 3} 
                  key={organisation.id}
                >
                  <OrganisationCard 
                    organisation={organisation} 
                    variant={viewMode}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Pagination */}
          {!loading && organizations.length > 0 && pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                showFirstLast
                showPrevNext
              />
            </Box>
          )}

          {/* Pricing Section */}
          <Box sx={{ mt: 8, mb: 6 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                sx={{ 
                  fontWeight: 700,
                  color: 'primary.main',
                  mb: 2
                }}
              >
                Tarification
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}
              >
                Un modèle transparent et équitable pour tous
              </Typography>
              <Chip 
                label="Version MVP - Plans tarifaires à venir" 
                color="info" 
                variant="outlined"
                sx={{ fontSize: '0.9rem', py: 1 }}
              />
            </Box>

            {/* Pricing Model Infographic */}
            <Paper elevation={3} sx={{ p: 4, mb: 6, borderRadius: 3 }}>
              <Typography 
                variant="h4" 
                component="h3" 
                sx={{ 
                  textAlign: 'center',
                  fontWeight: 600,
                  color: 'primary.main',
                  mb: 4
                }}
              >
                Comment ça fonctionne ?
              </Typography>

              {/* Flow Diagram */}
              <Box sx={{ position: 'relative' }}>
                <Grid container spacing={3} alignItems="center">
                  {/* Step 1: Structure */}
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ textAlign: 'center', p: 3, height: '100%', bgcolor: 'primary.light' }}>
                      <Business sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        1. Structure
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Prépare la mission
                      </Typography>
                      <Box sx={{ bgcolor: 'primary.light', p: 2, borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Taux horaire brut
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>+ Frais de service</Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Arrow 1 */}
                  <Grid item xs={12} md={1} sx={{ textAlign: 'center' }}>
                    <Box>
                      <TrendingUp sx={{ fontSize: 32, color: 'primary.main', transform: 'rotate(90deg)' }} />
                    </Box>
                  </Grid>

                  {/* Step 2: Professional */}
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ textAlign: 'center', p: 3, height: '100%', bgcolor: 'success.light' }}>
                      <Person sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        2. Indépendant
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Réalise la mission
                      </Typography>
                      <Box sx={{ bgcolor: 'success.light', p: 2, borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Mission terminée
                        </Typography>
                        <CheckCircle sx={{ fontSize: 20, mt: 1, color: 'success.main' }} />
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Arrow 2 */}
                  <Grid item xs={12} md={1} sx={{ textAlign: 'center' }}>
                    <Box>
                      <TrendingUp sx={{ fontSize: 32, color: 'primary.main', transform: 'rotate(90deg)' }} />
                    </Box>
                  </Grid>

                  {/* Step 3: Payment */}
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ textAlign: 'center', p: 3, height: '100%', bgcolor: 'warning.light' }}>
                      <Payment sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        3. Paiement
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Rémunération finale
                      </Typography>
                      <Box sx={{ bgcolor: 'warning.light', p: 2, borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Taux horaire net
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Après commission</Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Commission Details */}
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 4, height: '100%', borderRadius: 3, bgcolor: 'background.paper' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <AccountBalance sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Commission Mission Sport
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        fontWeight: 700,
                        color: 'primary.main',
                        mb: 1
                      }}
                    >
                      20%
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Commission prélevée sur chaque mission
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Cette commission permet de maintenir et développer la plateforme, 
                    d'assurer le support client et de garantir la sécurité des transactions.
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 4, height: '100%', borderRadius: 3, bgcolor: 'background.paper' }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                    Exemple de calcul
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Taux horaire brut :</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>50€/h</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Frais de service :</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>5€</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Total facturé :</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>55€</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: 'orange' }}>Commission (20%) :</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'orange' }}>-10€</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>Rémunération nette :</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>40€</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Future Plans Notice */}
            <Paper 
              sx={{ 
                p: 4, 
                mt: 6, 
                bgcolor: 'info.light', 
                borderRadius: 3,
                textAlign: 'center'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Plans tarifaires à venir
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.primary' }}>
                Nous travaillons actuellement sur des plans tarifaires adaptés aux différents 
                types d'utilisateurs. Ces options seront disponibles dans les prochaines versions 
                de la plateforme.
              </Typography>
            </Paper>
          </Box>
        </Container>

        {/* Mobile Filters Drawer */}
        <Drawer
          anchor="bottom"
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '90vh'
            }
          }}
        >
          <OrganisationFilters 
            isDrawer 
            onClose={() => setFiltersOpen(false)} 
          />
        </Drawer>

        {/* Mobile FAB for Filters */}
        {isMobile && (
          <Fab
            color="primary"
            aria-label="filtres"
            onClick={handleFiltersToggle}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: 1000
            }}
          >
            <FilterList />
            {getActiveFiltersCount() > 0 && (
              <Chip 
                label={getActiveFiltersCount()} 
                size="small" 
                color="secondary"
                sx={{ 
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  minWidth: 20,
                  height: 20,
                  fontSize: '0.75rem'
                }}
              />
            )}
          </Fab>
        )}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default Organisations;